"use client";

/**
 * Bloc « détails facultatifs » du formulaire de contact (champs optionnels).
 * Extrait de ContactForm pour garder chaque fichier < 400 lignes. Markup,
 * classes et attributs (id/name/type) identiques à l'origine — seuls les
 * libellés viennent du dict (CONTACT_UI[locale].form), via `t`.
 */
import type { FormState } from "@/(global)/contact/components/types";
import type { ContactUi } from "@/(global)/contact/components/contactDict";
import { FORM_FIELD_CLASS } from "@/config/forms";

const INPUT_CLASS = FORM_FIELD_CLASS.input;

type ContactDetailsFieldsProps = {
  form: FormState;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  showCompanyFields: boolean;
  t: ContactUi["form"];
};

type TextFieldProps = {
  id: keyof FormState;
  label: string;
  value: string;
  onChange: ContactDetailsFieldsProps["onChange"];
  type?: "date" | "time";
  inputMode?: "numeric";
  placeholder?: string;
};

const TextField = ({
  id,
  label,
  value,
  onChange,
  type,
  inputMode,
  placeholder,
}: TextFieldProps): JSX.Element => (
  <div>
    <label className={FORM_FIELD_CLASS.label} htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      inputMode={inputMode}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={INPUT_CLASS}
    />
  </div>
);

export const ContactDetailsFields = ({
  form,
  onChange,
  showCompanyFields,
  t,
}: ContactDetailsFieldsProps): JSX.Element => {
  return (
    <details className="rounded-xl border border-black/10 bg-black/[0.02] p-4">
      <summary className="cursor-pointer text-sm font-medium text-black/90">
        {t.detailsSummary}
      </summary>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {showCompanyFields && (
          <>
            <TextField
              id="companyName"
              label={t.company}
              value={form.companyName ?? ""}
              onChange={onChange}
            />
            <TextField
              id="eventName"
              label={t.eventName}
              value={form.eventName ?? ""}
              onChange={onChange}
            />
          </>
        )}
        <TextField
          id="pickup"
          label={t.pickup}
          value={form.pickup ?? ""}
          onChange={onChange}
        />
        <TextField
          id="dropoff"
          label={t.dropoff}
          value={form.dropoff ?? ""}
          onChange={onChange}
        />
        <TextField
          id="date"
          label={t.date}
          type="date"
          value={form.date ?? ""}
          onChange={onChange}
        />
        <TextField
          id="time"
          label={t.time}
          type="time"
          value={form.time ?? ""}
          onChange={onChange}
        />
        <TextField
          id="passengers"
          label={t.passengers}
          inputMode="numeric"
          value={form.passengers ?? ""}
          onChange={onChange}
        />
        <TextField
          id="vehicle"
          label={t.vehicle}
          value={form.vehicle ?? ""}
          onChange={onChange}
          placeholder={t.vehiclePlaceholder}
        />
        <TextField
          id="budget"
          label={t.budget}
          value={form.budget ?? ""}
          onChange={onChange}
          placeholder={t.budgetPlaceholder}
        />
      </div>
    </details>
  );
};
